import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext-public';
import { useLanguage } from '../contexts/LanguageContext';

const SignUp = () => {
  const { signUp, confirmSignUp, loading, error } = useAuth();
  const { t } = useLanguage();
  const [step, setStep] = useState('signup'); // 'signup' or 'confirm'
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    name: '',
    phone: ''
  });
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 'signup') {
      // Validation
      if (formData.password !== formData.confirmPassword) {
        alert(t('passwordsDoNotMatch'));
        return;
      }
      
      if (formData.password.length < 8) {
        alert(t('passwordTooShort'));
        return;
      }

      try {
        const result = await signUp({
          username: formData.username,
          password: formData.password,
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
          role: 'member' // Default role for new signups
        });
        
        if (result.needsConfirmation) {
          setStep('confirm');
        }
      } catch (err) {
        console.error('Signup error:', err);
      }
    } else if (step === 'confirm') {
      try {
        await confirmSignUp(formData.username, confirmationCode);
        alert(t('accountConfirmed'));
        // Will redirect to login automatically
      } catch (err) {
        console.error('Confirmation error:', err);
      }
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="text-6xl mb-4">🎮</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('appName')}
          </h2>
          <p className="text-gray-600 mb-8">
            {t('appSlogan')}
          </p>
          <h3 className="text-xl font-semibold text-gray-800">
            {step === 'signup' ? t('createYourAccount') : t('confirmYourAccount')}
          </h3>
        </div>

        {/* Demo Notice */}
        {step === 'signup' && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-green-600">🌐</span>
              <h4 className="font-medium text-green-800">Demo Sign Up</h4>
            </div>
            <p className="text-sm text-green-700">
              This demonstrates the production sign-up flow. In production, you'll receive a real email confirmation.
            </p>
          </div>
        )}

        {/* Sign Up Form */}
        {step === 'signup' && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Username Field */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('username')} *
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('enterUsername')}
                />
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('email')} *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('enterEmail')}
                />
              </div>

              {/* Full Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('fullName')} *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('enterFullName')}
                />
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('phoneNumber')}
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('enterPhoneNumber')}
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('password')} *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder={t('enterPassword')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('passwordRequirements')}</p>
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('confirmPassword')} *
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('confirmYourPassword')}
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('creatingAccount')}...
                </div>
              ) : (
                t('createAccount')
              )}
            </button>

            {/* Sign In Link */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t('alreadyHaveAccount')}{' '}
                <Link 
                  to="/login" 
                  className="font-medium text-green-600 hover:text-green-500"
                >
                  {t('signIn')}
                </Link>
              </p>
            </div>
          </form>
        )}

        {/* Confirmation Form */}
        {step === 'confirm' && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="text-center mb-6">
              <div className="text-4xl mb-4">📧</div>
              <p className="text-gray-600 mb-4">
                {t('confirmationCodeSent')} <strong>{formData.email}</strong>
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Demo:</strong> Enter any 6-digit code (e.g., 123456) to continue
                </p>
              </div>
            </div>

            <div>
              <label htmlFor="confirmationCode" className="block text-sm font-medium text-gray-700 mb-1">
                {t('confirmationCode')}
              </label>
              <input
                id="confirmationCode"
                name="confirmationCode"
                type="text"
                required
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-lg tracking-widest"
                placeholder="123456"
                maxLength="6"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('confirming')}...
                </div>
              ) : (
                t('confirmAccount')
              )}
            </button>

            {/* Back to Sign Up */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setStep('signup')}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                ← {t('backToSignUp')}
              </button>
            </div>
          </form>
        )}

        {/* Production Notice */}
        <div className="text-center text-xs text-gray-500 mt-8 p-3 bg-gray-50 rounded-lg">
          <p className="mb-1">🔗 <strong>Production Ready:</strong> This demo shows the exact production interface</p>
          <p>Real version uses AWS Cognito with email verification and secure password policies</p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;